import { Component, ElementRef, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { PageTitleService } from '../../../shared/services';
import { AgendaService } from '../../../services/business';
import { CountryService } from '../../../services';
import { AgendaEvent, Client, ClientInput } from '../../../shared/interfaces';
import { Modal } from '../../../shared/components/modal/modal';
import { CreatesByBusiness } from '../../../shared/common/CreatesByBusiness.class';
import { PeopleAdd } from '../people-add/people-add';
import { parseDbUtc } from '../../../shared/common/utils';
import { ClientPaymentHistoryComponent } from '../client-payment-history/client-payment-history';
import { ClientCredit } from '../client-credit/client-credit';

/** Appointment with its dates parsed once, used across the page. */
interface Visit {
  eventId: string;
  start: Date;
  end: Date;
  serviceName: string;
  professionalName: string;
  statusCode: string;
  statusName: string;
  price: number | null;
  paymentCompleted: boolean;
  notes: string | null;
}

type VisitFilter = 'past' | 'upcoming' | 'cancelled' | 'all';

/** Statuses that never count as a visit or an upcoming appointment. */
const NOT_A_VISIT = new Set(['cancelled', 'hidden', 'pending_client']);

@Component({
  selector: 'app-people-profile',
  imports: [Modal, CommonModule, ClientPaymentHistoryComponent, ClientCredit],
  templateUrl: './people-profile.html',
  styleUrls: ['./people-shared.css', './people-profile.css'],
})
export class PeopleProfile extends CreatesByBusiness<Client, any | ClientInput> implements OnInit {
  protected override endpoint: string = 'clients';

  @ViewChild('visitsDialog') visitsDialog?: ElementRef<HTMLDialogElement>;

  private titleService = inject(PageTitleService);
  private route = inject(ActivatedRoute);
  private agendaService = inject(AgendaService);
  private countryService = inject(CountryService);
  private countries = this.countryService.getStore().getModel();

  private static readonly RECENT_COUNT = 4;
  private static readonly DIALOG_PAGE = 30;

  PeopleEditForm = PeopleAdd;
  modalInputs: Record<string, unknown> = {};
  userId: string | null = null;

  isLoadingClient = signal(false);
  isLoadingEvents = signal(false);
  eventsFailed = signal(false);
  client = signal<Client | null>(null);
  rawEvents = signal<AgendaEvent[]>([]);
  now = signal(new Date());

  // ---- Derived data ----

  visits = computed<Visit[]>(() =>
    this.rawEvents()
      .map((e) => ({
        eventId: e.eventId,
        start: parseDbUtc(e.startDate),
        end: parseDbUtc(e.endDate),
        serviceName: e.serviceName || 'Servicio',
        professionalName: e.professionalName || e.agendaName || '',
        statusCode: e.statusCode,
        statusName: e.statusName,
        price: e.finalPrice ?? e.servicePrice ?? null,
        paymentCompleted: !!e.paymentCompleted,
        notes: e.notes ?? null,
      }))
      .filter((v) => v.statusCode !== 'hidden')
      .sort((a, b) => b.start.getTime() - a.start.getTime())
  );

  /** Past appointments that actually happened (newest first). */
  pastVisits = computed(() =>
    this.visits().filter((v) => v.start < this.now() && !NOT_A_VISIT.has(v.statusCode))
  );
  upcoming = computed(() =>
    this.visits()
      .filter((v) => v.start >= this.now() && !NOT_A_VISIT.has(v.statusCode))
      .sort((a, b) => a.start.getTime() - b.start.getTime())
  );
  cancelled = computed(() => this.visits().filter((v) => v.statusCode === 'cancelled'));
  nextVisit = computed(() => this.upcoming()[0] ?? null);
  lastVisit = computed(() => this.pastVisits()[0] ?? null);
  firstVisit = computed(() => this.pastVisits().at(-1) ?? null);
  recentVisits = computed(() => this.pastVisits().slice(0, PeopleProfile.RECENT_COUNT));

  favoriteService = computed(() => this.mostCommon(this.pastVisits().map((v) => v.serviceName)));
  favoriteProfessional = computed(() =>
    this.mostCommon(this.pastVisits().map((v) => v.professionalName).filter(Boolean))
  );

  /** Average days between consecutive visits; needs at least 3 visits to mean something. */
  visitEveryDays = computed<number | null>(() => {
    const visits = this.pastVisits();
    if (visits.length < 3) return null;
    const spanDays = (visits[0].start.getTime() - visits[visits.length - 1].start.getTime()) / 86_400_000;
    return Math.max(1, Math.round(spanDays / (visits.length - 1)));
  });

  daysSinceLastVisit = computed<number | null>(() => {
    const last = this.lastVisit();
    if (!last) return null;
    return Math.floor((this.now().getTime() - last.start.getTime()) / 86_400_000);
  });

  /** True when the client is noticeably past their usual return time. */
  isOverdue = computed(() => {
    const every = this.visitEveryDays();
    const since = this.daysSinceLastVisit();
    return every !== null && since !== null && !this.nextVisit() && since > every * 1.5;
  });

  /** Visits per month for the last 12 months, oldest first. */
  rhythm = computed(() => {
    const now = this.now();
    const months = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
      return { key: `${d.getFullYear()}-${d.getMonth()}`, date: d, count: 0 };
    });
    const byKey = new Map(months.map((m) => [m.key, m]));
    for (const v of this.pastVisits()) {
      const m = byKey.get(`${v.start.getFullYear()}-${v.start.getMonth()}`);
      if (m) m.count++;
    }
    const max = Math.max(1, ...months.map((m) => m.count));
    return months.map((m) => ({ ...m, height: m.count ? Math.max(18, (m.count / max) * 100) : 0 }));
  });
  visitsLastYear = computed(() => this.rhythm().reduce((sum, m) => sum + m.count, 0));

  whatsappUrl = computed(() => {
    const c = this.client();
    const digits = (c?.phone ?? '').replace(/\D/g, '');
    if (!digits) return null;
    const dial = (this.countries().find((x) => x.countryId === c?.countryId)?.dialCode ?? '').replace(/\D/g, '');
    const full = dial && !digits.startsWith(dial) ? dial + digits : digits;
    return `https://wa.me/${full}`;
  });

  initials = computed(() => {
    const c = this.client();
    return `${c?.firstName?.[0] ?? ''}${c?.lastName?.[0] ?? ''}`.toUpperCase() || '?';
  });

  fullName = computed(() => {
    const c = this.client();
    return [c?.firstName, c?.lastName].filter(Boolean).join(' ');
  });

  // ---- Visits dialog ----

  filter = signal<VisitFilter>('past');
  search = signal('');
  dialogLimit = signal(PeopleProfile.DIALOG_PAGE);

  filterCounts = computed<Record<VisitFilter, number>>(() => ({
    past: this.pastVisits().length,
    upcoming: this.upcoming().length,
    cancelled: this.cancelled().length,
    all: this.visits().length,
  }));

  private filteredVisits = computed(() => {
    const source = {
      past: this.pastVisits(),
      upcoming: this.upcoming(),
      cancelled: this.cancelled(),
      all: this.visits(),
    }[this.filter()];
    const term = this.normalize(this.search());
    if (!term) return source;
    return source.filter((v) => this.normalize(`${v.serviceName} ${v.professionalName}`).includes(term));
  });

  /** Filtered visits grouped by month, limited for long histories. */
  dialogGroups = computed(() => {
    const groups: { label: string; visits: Visit[] }[] = [];
    for (const v of this.filteredVisits().slice(0, this.dialogLimit())) {
      const label = v.start.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
      const last = groups.at(-1);
      if (last?.label === label) last.visits.push(v);
      else groups.push({ label, visits: [v] });
    }
    return groups;
  });
  dialogTotal = computed(() => this.filteredVisits().length);

  readonly filterOptions: { value: VisitFilter; label: string }[] = [
    { value: 'past', label: 'Asistidas' },
    { value: 'upcoming', label: 'Próximas' },
    { value: 'cancelled', label: 'Canceladas' },
    { value: 'all', label: 'Todas' },
  ];

  ngOnInit(): void {
    this.titleService.setPage({
      title: 'Ficha del cliente',
      subtitle: 'Datos de contacto, citas y pagos de este cliente',
      breadcrumbs: [{ name: 'Clientes', url: '/people' }],
    });
    this.countryService.fetchAndSetEntitiesToStore();
    this.userId = this.route.snapshot.paramMap.get('userId');
    if (this.userId) {
      this.loadEntity(this.userId);
      this.loadEvents(this.userId);
    }
  }

  loadEntity(entityId: string): void {
    this.isLoadingClient.set(true);
    this.businessService.getRecordByBusinessId<Client>(this.businessId, entityId, this.endpoint).subscribe({
      next: (res) => {
        this.client.set(res);
        this.isLoadingClient.set(false);
      },
      error: () => {
        this.isLoadingClient.set(false);
        this.toastService.show('No pudimos cargar los datos del cliente.', 'warning');
      },
    });
  }

  /** Loads the full appointment history of the client in this business. */
  loadEvents(userId: string): void {
    this.isLoadingEvents.set(true);
    this.eventsFailed.set(false);
    this.agendaService.getAgendaEvents({ clientId: userId, businessId: this.businessId }).subscribe({
      next: (list) => {
        this.rawEvents.set(list);
        this.now.set(new Date());
        this.isLoadingEvents.set(false);
      },
      error: () => {
        this.isLoadingEvents.set(false);
        this.eventsFailed.set(true);
      },
    });
  }

  onEditClient(): void {
    this.modalInputs = { entityId: this.userId, endpoint: this.endpoint };
    this.open = true;
  }

  protected override afterUpdate(): void {
    this.toastService.show('Cliente actualizado', 'success');
    this.loadEntity(this.userId!);
    this.open = false;
  }

  openVisits(filter: VisitFilter = 'past'): void {
    this.filter.set(filter);
    this.search.set('');
    this.dialogLimit.set(PeopleProfile.DIALOG_PAGE);
    this.visitsDialog?.nativeElement.showModal();
  }

  closeVisits(): void {
    this.visitsDialog?.nativeElement.close();
  }

  setFilter(filter: VisitFilter): void {
    this.filter.set(filter);
    this.dialogLimit.set(PeopleProfile.DIALOG_PAGE);
  }

  showMoreVisits(): void {
    this.dialogLimit.update((n) => n + PeopleProfile.DIALOG_PAGE);
  }

  onDialogClick(event: MouseEvent, dialog: HTMLDialogElement): void {
    if (event.target === dialog) dialog.close();
  }

  // ---- Formatting helpers ----

  relativeDays(date: Date): string {
    const today = new Date(this.now().getFullYear(), this.now().getMonth(), this.now().getDate());
    const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diff = Math.round((day.getTime() - today.getTime()) / 86_400_000);
    if (diff === 0) return 'hoy';
    if (diff === 1) return 'mañana';
    if (diff === -1) return 'ayer';
    if (diff > 0) return diff < 30 ? `en ${diff} días` : `en ${Math.round(diff / 30)} ${Math.round(diff / 30) === 1 ? 'mes' : 'meses'}`;
    const ago = -diff;
    if (ago < 30) return `hace ${ago} días`;
    if (ago < 365) {
      const m = Math.round(ago / 30);
      return `hace ${m} ${m === 1 ? 'mes' : 'meses'}`;
    }
    const y = Math.floor(ago / 365);
    return `hace ${y} ${y === 1 ? 'año' : 'años'}`;
  }

  formatMoney(value: number): string {
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(value);
  }

  statusTone(code: string): string {
    switch (code) {
      case 'completed':
        return 'done';
      case 'confirmed':
        return 'confirmed';
      case 'cancelled':
        return 'cancelled';
      default:
        return 'scheduled';
    }
  }

  private mostCommon(values: string[]): { name: string; count: number } | null {
    const counts = new Map<string, number>();
    for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
    let best: { name: string; count: number } | null = null;
    for (const [name, count] of counts) {
      if (!best || count > best.count) best = { name, count };
    }
    return best;
  }

  private normalize(value: string): string {
    return value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
  }
}
